import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import {
  adminGetDeploymentSettingsOptions,
  adminGetDeploymentSettingsQueryKey,
  adminListAgentBundlesOptions,
  adminListAgentBundlesQueryKey,
  adminListExtensionReleasesOptions,
  adminListExtensionReleasesQueryKey,
  adminUpdateDeploymentSettingsMutation,
  adminUploadAgentBundleMutation,
  adminUploadExtensionReleaseMutation,
} from '@/client/@tanstack/react-query.gen'
import type { DeploymentSettingRead } from '@/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { sha256Hex, validateAgentBundle } from '@/lib/agent-bundle'

interface SelectedBundle {
  file: File
  version: string
  sha256: string
}

interface SelectedCrx {
  file: File
  sha256: string
}

function FleetDefaultControl({ setting }: { setting: DeploymentSettingRead }) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState(setting.default_target_version ?? '')
  const update = useMutation({
    ...adminUpdateDeploymentSettingsMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminGetDeploymentSettingsQueryKey() })
    },
  })

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        const trimmed = value.trim()
        update.mutate({ body: { default_target_version: trimmed === '' ? null : trimmed } })
      }}
      className="flex flex-wrap items-end gap-2"
    >
      <label className="text-sm">
        <span className="text-muted-foreground mr-2">Fleet default version</span>
        <Input
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="No default pinned"
          className="inline-block w-40"
        />
      </label>
      <Button type="submit" size="sm" disabled={update.isPending}>
        Update fleet default
      </Button>
    </form>
  )
}

export function AdminAgentReleases() {
  const queryClient = useQueryClient()
  const bundles = useQuery(adminListAgentBundlesOptions())
  const settings = useQuery(adminGetDeploymentSettingsOptions())
  const extensions = useQuery(adminListExtensionReleasesOptions())
  const upload = useMutation({
    ...adminUploadAgentBundleMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminListAgentBundlesQueryKey() })
    },
  })
  const extensionUpload = useMutation({
    ...adminUploadExtensionReleaseMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminListExtensionReleasesQueryKey() })
    },
  })

  const fileInput = useRef<HTMLInputElement>(null)
  const [selected, setSelected] = useState<SelectedBundle | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [notes, setNotes] = useState('')

  const crxInput = useRef<HTMLInputElement>(null)
  const [crx, setCrx] = useState<SelectedCrx | null>(null)
  const [crxError, setCrxError] = useState<string | null>(null)
  const [crxVersion, setCrxVersion] = useState('')
  const [crxFingerprint, setCrxFingerprint] = useState('')

  async function handleFile(file: File | undefined): Promise<void> {
    setSelected(null)
    setFileError(null)
    if (!file) {
      return
    }
    const result = await validateAgentBundle(file)
    if (!result.ok) {
      setFileError(result.message)
      return
    }
    setSelected({ file, version: result.version, sha256: result.sha256 })
  }

  function submit(): void {
    if (!selected) {
      setFileError('Select a tradval-agent-update-<version> bundle first.')
      return
    }
    upload.mutate(
      {
        body: {
          version: selected.version,
          sha256: selected.sha256,
          notes: notes || null,
          binary: selected.file,
        },
      },
      {
        onSuccess: () => {
          setSelected(null)
          setNotes('')
          if (fileInput.current) {
            fileInput.current.value = ''
          }
        },
      },
    )
  }

  async function handleCrx(file: File | undefined): Promise<void> {
    setCrx(null)
    setCrxError(null)
    if (!file) {
      return
    }
    if (!file.name.endsWith('.crx')) {
      setCrxError('Expected a packed .crx file.')
      return
    }
    setCrx({ file, sha256: await sha256Hex(await file.arrayBuffer()) })
  }

  function submitCrx(): void {
    if (!crx) {
      setCrxError('Select a .crx file first.')
      return
    }
    if (crxVersion.trim() === '') {
      setCrxError('Enter the extension version.')
      return
    }
    extensionUpload.mutate(
      {
        body: {
          version: crxVersion.trim(),
          sha256: crx.sha256,
          key_fingerprint: crxFingerprint.trim() || null,
          crx: crx.file,
        },
      },
      {
        onSuccess: () => {
          setCrx(null)
          setCrxVersion('')
          setCrxFingerprint('')
          if (crxInput.current) {
            crxInput.current.value = ''
          }
        },
      },
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <h1 className="text-2xl font-bold tracking-tight">Agent releases</h1>

      <section className="space-y-3 rounded-lg border p-4">
        <h2 className="text-lg font-semibold">Fleet default version</h2>
        <p className="text-muted-foreground text-sm">
          The version unpinned devices follow. Leave empty to pin no default.
        </p>
        {settings.data && <FleetDefaultControl setting={settings.data} />}
      </section>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
        noValidate
        className="space-y-3 rounded-lg border p-4"
      >
        <h2 className="text-lg font-semibold">Upload bundle</h2>
        {upload.isError && (
          <p className="text-destructive text-sm" role="alert">
            Upload failed — that version may already exist.
          </p>
        )}
        <label className="block text-sm">
          <span className="text-muted-foreground">Bundle</span>
          <Input
            ref={fileInput}
            type="file"
            onChange={(event) => {
              void handleFile(event.target.files?.[0])
            }}
          />
        </label>
        {fileError && (
          <p className="text-destructive text-sm" role="alert">
            {fileError}
          </p>
        )}
        {selected && (
          <dl className="space-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="text-muted-foreground">Version</dt>
              <dd className="font-medium">{selected.version}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground">SHA256</dt>
              <dd className="font-mono break-all text-xs">{selected.sha256}</dd>
            </div>
          </dl>
        )}
        <label className="block text-sm">
          <span className="text-muted-foreground">Notes</span>
          <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={2} />
        </label>
        <Button type="submit" disabled={!selected || upload.isPending}>
          {upload.isPending ? 'Uploading…' : 'Upload bundle'}
        </Button>
      </form>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Version</TableHead>
            <TableHead>SHA256</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead>Released</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bundles.data?.map((rel) => (
            <TableRow key={rel.version}>
              <TableCell className="font-medium">{rel.version}</TableCell>
              <TableCell className="max-w-40 truncate font-mono text-xs">{rel.sha256}</TableCell>
              <TableCell>{rel.notes}</TableCell>
              <TableCell className="text-muted-foreground">
                {new Date(rel.released_at).toLocaleDateString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {bundles.isSuccess && bundles.data.length === 0 && (
        <p className="text-muted-foreground">No bundles uploaded yet.</p>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault()
          submitCrx()
        }}
        noValidate
        className="space-y-3 rounded-lg border p-4"
      >
        <h2 className="text-lg font-semibold">Upload extension</h2>
        {extensionUpload.isError && (
          <p className="text-destructive text-sm" role="alert">
            Upload failed — that version may already exist.
          </p>
        )}
        <label className="block text-sm">
          <span className="text-muted-foreground">Extension (.crx)</span>
          <Input
            ref={crxInput}
            type="file"
            accept=".crx"
            onChange={(event) => {
              void handleCrx(event.target.files?.[0])
            }}
          />
        </label>
        {crxError && (
          <p className="text-destructive text-sm" role="alert">
            {crxError}
          </p>
        )}
        {crx && (
          <dl className="space-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="text-muted-foreground">SHA256</dt>
              <dd className="font-mono break-all text-xs">{crx.sha256}</dd>
            </div>
          </dl>
        )}
        <label className="block text-sm">
          <span className="text-muted-foreground">Extension version</span>
          <Input
            value={crxVersion}
            onChange={(event) => setCrxVersion(event.target.value)}
            className="inline-block w-40"
          />
        </label>
        <label className="block text-sm">
          <span className="text-muted-foreground">Key fingerprint</span>
          <Input
            value={crxFingerprint}
            onChange={(event) => setCrxFingerprint(event.target.value)}
            className="inline-block w-72"
          />
        </label>
        <Button type="submit" disabled={!crx || extensionUpload.isPending}>
          {extensionUpload.isPending ? 'Uploading…' : 'Upload extension'}
        </Button>
      </form>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Version</TableHead>
            <TableHead>SHA256</TableHead>
            <TableHead>Key fingerprint</TableHead>
            <TableHead>Released</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {extensions.data?.map((ext) => (
            <TableRow key={ext.version}>
              <TableCell className="font-medium">{ext.version}</TableCell>
              <TableCell className="max-w-40 truncate font-mono text-xs">{ext.sha256}</TableCell>
              <TableCell className="font-mono text-xs">{ext.key_fingerprint}</TableCell>
              <TableCell className="text-muted-foreground">
                {new Date(ext.released_at).toLocaleDateString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {extensions.isSuccess && extensions.data.length === 0 && (
        <p className="text-muted-foreground">No extensions uploaded yet.</p>
      )}
    </div>
  )
}
